import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { PartType } from '../../../../../shared/enums/part-type';

import { DomSanitizer } from '@angular/platform-browser';

import { initFlowbite } from 'flowbite';
import { ICONS_RAW } from '../../../../../shared/constants/icons-raw';
import { ReviewRequest } from '../../../../../shared/models/dtc/reviewRequest';
import { Decision } from '../../../../../shared/models/internal/decision';
import { Paragraph } from '../../../../../shared/models/internal/popover/paragraph';
import { CommonUtils, errorToString, pascalCaseToWords } from '../../../../common/common-utils';
import { ReviewRequestApiService } from '../../../../services/api/review-request-api.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { HelpPopoverComponent } from '../../popover/help-popover/help-popover.component';
import { DeleteIconComponent } from '../../../icons/delete-icon/delete-icon.component';
import { ThumbUpIconComponent } from '../../../icons/thumb-up-icon/thumb-up-icon.component';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { Reviewable } from '../../../../../shared/models/base/reviewable';
import { DiffField } from '../../../../../shared/models/internal/diffs/diff-field';
import { DiffViewer } from '../../diff/diff-viewer/diff-viewer';
import { SwPartNumber } from '../../../../../shared/models/sw-part-number/sw-part-number';
import { SwPartNumberState } from '../../../../../shared/enums/sw-part-number-state';
import { ExecutionType } from '../../../modals/propagation-modal/execution-type';
import { MergeReviewPropagationModal } from '../../../modals/propagation-modal/merge-review-propagation-modal';
import { DeleteReviewPropagationModal } from '../../../modals/propagation-modal/delete-review-propagation-modal';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { Policy } from '../../../../../shared/models/info/policy';
import { ReviewerRole } from '../../../../../shared/enums/reviewer-role';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-review-request-merge',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ThumbUpIconComponent,
    DeleteIconComponent,
    DecisionModalComponent,
    HelpPopoverComponent,
    DiffViewer,
    MergeReviewPropagationModal,
    DeleteReviewPropagationModal,
    AgentActionDirective,
  ],
  templateUrl: './review-request-merge.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './review-request-merge.component.scss',
})
export class ReviewRequestMergeComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() public reviewable?: Reviewable;
  @Input() public swPartNumber?: SwPartNumber;
  @Output() public reviewableUpdated = new EventEmitter<Reviewable>();
  @ViewChild('mergeSiblingsModal') public mergeSiblingsModal!: MergeReviewPropagationModal;
  @ViewChild('deleteReviewPropagationModal') public deleteReviewPropagationModal!: DeleteReviewPropagationModal;

  public commonUtils = CommonUtils;
  public partType = PartType;
  public displayedReview?: ReviewRequest;
  public canMerge = false;
  public canDelete = false;
  public reviewRole = '';
  public reviewNotes = '';
  public diffFields: DiffField[] = [];
  public paragraphs: Paragraph[] = [];
  protected readonly executionType = ExecutionType;

  private afterSalesRoleEnabled = false;
  private technicalOrganisationRoleEnabled = false;
  private mergeReviewRequestsEnabled = false;

  private dtcReviewRequestApi = inject(ReviewRequestApiService);
  private toast = inject(HotToastService);
  private appInsightsService = inject(AppInsightsService);
  private accessRightsService = inject(AccessRightsService);
  private readonly sanitizer = inject(DomSanitizer);

  public ngOnInit(): void {
    this.displayedReview = undefined;
    this.buildHelpParagraphs();

    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.afterSalesRoleEnabled = ar.authorized(Policy.canDeleteAfterSalesDtcReviewRequests);
      this.technicalOrganisationRoleEnabled = ar.authorized(Policy.canDeleteTechnicalOrganisationDtcReviewRequests);
      this.mergeReviewRequestsEnabled = ar.authorized(Policy.canMergeDtcReviewRequests);
    });

    this.init();
  }

  public ngAfterViewInit(): void {
    initFlowbite();
    this.appInsightsService.logSidebarUsage('DTC_merge_review_requests');
  }

  public ngOnChanges(): void {
    this.init();
  }

  public mergeReview(): void {
    if (!this.displayedReview) {
      return;
    }

    this.dtcReviewRequestApi.mergeReview(this.displayedReview).subscribe({
      next: (result) => {
        this.reviewable!.reviewRequest = result.reviewRequest;
        this.displayReviewIfAny();
        this.toast.success('Review merged successfully');
        this.displayedReview = undefined;
        this.reviewableUpdated.emit(this.reviewable);

        if (result.affectedDtcSiblings?.length ?? 0 > 0) {
          this.mergeSiblingsModal.openModal(this.reviewable!.id, result?.affectedDtcSiblings);
        }
      },
      error: (error) => {
        console.error('Error merging review:', errorToString(error));
      },
    });
  }

  public onDiscardReviewDecision(decision: Decision): void {
    if (decision.result) {
      this.discardReview();
    }
  }

  private discardReview(): void {
    if (!this.displayedReview) {
      return;
    }

    this.dtcReviewRequestApi.deleteReview(this.displayedReview).subscribe({
      next: (reviewRequestResult) => {
        this.reviewable!.reviewRequest = reviewRequestResult?.reviewRequest;
        this.displayReviewIfAny();
        this.toast.success('Review deleted successfully');
        this.reviewableUpdated.emit(this.reviewable);
        this.deleteReviewPropagationModal.openModal(
          reviewRequestResult?.reviewRequest?.id,
          reviewRequestResult?.affectedDtcSiblings
        );
        this.init();
      },
      error: (error) => {
        console.error('Error deleting review:', errorToString(error));
      },
    });
  }

  private init(): void {
    if (!this.reviewable) {
      this.displayedReview = undefined;
      this.canMerge = false;
      this.canDelete = false;
      return;
    }

    this.reviewable.reviewRequest = undefined; // get rid of old transient review requests
    this.displayedReview = undefined;
    this.fetchReviews();
  }

  private fetchReviews(): void {
    if (!this.reviewable?.id) {
      return;
    }
    this.dtcReviewRequestApi.getReviews(this.reviewable.id).subscribe({
      next: (reviews) => {
        this.reviewable!.reviewRequest = reviews[0]; // TODO: 779: this should be refactored
        this.displayReviewIfAny();
      },
      error: (error) => {
        console.error('Error fetching reviews:', errorToString(error));
      },
    });
  }

  private displayReviewIfAny(): void {
    const review = this.reviewable!.reviewRequest;

    if (!review) {
      this.canMerge = false;
      this.canDelete = false;
      return;
    }

    const reviewableIsSnapshotDtc = this.swPartNumber?.state == SwPartNumberState.Snapshot;
    this.canMerge = !reviewableIsSnapshotDtc && this.mergeReviewRequestsEnabled;
    this.canDelete = this.evaluateCanDelete(review.reviewerRole);

    this.displayReview(review);
  }

  private evaluateCanDelete(reviewerRole: string | undefined): boolean {
    if (!reviewerRole) {
      return false;
    }

    switch (reviewerRole) {
      case ReviewerRole.AfterSales:
        return this.afterSalesRoleEnabled;
      case ReviewerRole.TechnicalOrganisation:
        return this.technicalOrganisationRoleEnabled;
      default:
        return false;
    }
  }

  private displayReview(review?: ReviewRequest): void {
    this.displayedReview = review;
    this.reviewRole = pascalCaseToWords(review?.reviewerRole ?? '');
    this.reviewNotes = review?.reviewNotes ?? '';

    if (!this.reviewable) {
      return;
    }

    const specs = [
      ['Cause (farmer)', this.reviewable.causeFarmer, review?.causeFarmer],
      ['Remedy (farmer)', this.reviewable.remedyFarmer, review?.remedyFarmer],
      ['Cause (dealer)', this.reviewable.causeDealer, review?.causeDealer],
      ['Remedy (dealer)', this.reviewable.remedyDealer, review?.remedyDealer],
      ['Cause (intern)', this.reviewable.causeIntern, review?.causeIntern],
      ['Remedy (intern)', this.reviewable.remedyIntern, review?.remedyIntern],
    ] as const;

    this.diffFields = specs.map(([label, left, right]) => DiffField.create(label, left, right));
  }

  private buildHelpParagraphs(): void {
    this.paragraphs = [
      {
        title: 'Explanation',
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.thumbUpButton),
        content: 'Accept suggested changes and update DTC',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.discardReviewButton),
        content: 'Discard review',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.diffAddedTextIcon),
        content: 'Text added by reviewer',
      },
      {
        header: this.sanitizer.bypassSecurityTrustHtml(ICONS_RAW.diffRemovedTextIcon),
        content: 'Text deleted by reviewer',
      },
    ];
  }
}
